import 'isomorphic-fetch';
import queryString from 'query-string';
import { removeWhitespaceFromValues } from 'common/utils';
import { splitTreatmentIndex } from './SplitTreatmentIndex';
import { AgeVerificationUtility } from '../apps/age_verification/utilities';

const sanitizeAddOns = addOns => addOns.filter(addOn => addOn.quantity > 0);
export const offlineError =
  'Error: no response received from request. The browser may be offline';
const MEMBERSHIPS_API = '/api/v1/memberships';

export class ApiError extends Error {
  constructor(error, ...params) {
    const message = error.message || `Error: ${error}`;

    super(message, ...params);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }

    this.data = error;
  }
}

export class ApiClient {
  constructor() {
    this.setCsrfToken();
  }

  setCsrfToken() {
    this.csrfToken = this.csrfToken || this.getCsrfToken();
  }

  getCsrfToken() {
    return (
      (typeof global.document !== 'undefined' &&
        global.document.querySelector('meta[name="csrf-token"]')) ||
      {}
    ).content;
  }

  makeRequest(url, options, queryParams = {}) {
    const params = queryString.stringify(queryParams, {
      arrayFormat: 'bracket',
    });
    const paramsAreEmpty = Object.keys(params).length === 0;
    const query = paramsAreEmpty ? '' : `?${params}`;

    return fetch(`${url}${query}`, this.buildRequestOptions(options));
  }

  buildRequestOptions(options = {}) {
    return {
      ...options,
      credentials: 'same-origin',
      headers: {
        ...options.headers,
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': this.csrfToken,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };
  }

  handleResponse(request) {
    return request.then(
      response => {
        const contentType = response.headers.get('content-type');
        const isJson = contentType && contentType.includes('application/json');
        const contentPromise = isJson ? response.json() : response.text();

        if (response.status >= 400) {
          return contentPromise.then(content => {
            throw new ApiError(content.error || content);
          });
        }
        return contentPromise;
      },
      () => {
        throw new ApiError(offlineError);
      }
    );
  }

  localeParams() {
    if (window.gon.current_store.available_locales.length > 1)
      return { locale: window.translations.locale };

    return {};
  }

  get(url, options = {}, queryParams = {}) {
    const request = this.makeRequest(url, options, queryParams);
    return this.handleResponse(request);
  }

  bodyToJson(body) {
    return body ? JSON.stringify(body) : body;
  }

  post(url, body) {
    const request = this.makeRequest(url, {
      method: 'POST',
      body: this.bodyToJson(body),
    });
    return this.handleResponse(request);
  }

  put(url, body, queryParams) {
    const request = this.makeRequest(
      url,
      {
        method: 'PUT',
        body: this.bodyToJson(body),
      },
      queryParams
    );
    return this.handleResponse(request);
  }

  patch(url, body) {
    const request = this.makeRequest(url, {
      method: 'PATCH',
      body: this.bodyToJson(body),
    });
    return this.handleResponse(request);
  }

  del(url) {
    const request = this.makeRequest(url, {
      method: 'DELETE',
    });
    return this.handleResponse(request);
  }

  // API calls.

  createAddress = address => {
    return this.post(
      '/api/v1/user_addresses',
      removeWhitespaceFromValues(address)
    );
  };

  updateAddress = address => {
    return this.put(
      `/api/v1/user_addresses/${address.id}`,
      removeWhitespaceFromValues(address)
    );
  };

  deleteAddress = id => {
    return this.del(`/api/v1/user_addresses/${id}`);
  };

  fetchAddress = id => {
    return this.get(`/api/v1/user_addresses/${id}`);
  };

  fetchAddresses = () => {
    return this.get('/api/v1/user_addresses');
  };

  fetchSubscribablePods = () => {
    // this request can be made from a logged out state and only returns
    // pods that could be subscribable
    return this.get('/api/v1/subscribable_pods');
  };

  checkBuildYourPlanErrorState = pods_quantity => {
    // this request can also be made from a logged out state
    return this.post('/api/v1/plan_errors', { pods_quantity });
  };

  fetchSubscription = () => {
    return this.get('/api/v1/subscription');
  };

  fetchSplitTreatments = (splits = splitTreatmentIndex) => {
    return this.get(`/api/v1/split`, {}, { features: splits });
  };

  fetchComplianceRestrictedStates = () => {
    return this.get('/api/v1/subscription/compliance_restricted_states');
  };

  applyCouponToOrder = couponCode => {
    return this.put('/api/v1/order_coupon_applications', {
      coupon_code: couponCode,
    });
  };

  applyCouponToSession = couponCode => {
    return this.put('/api/v1/session_coupon_applications', {
      coupon_code: couponCode,
    });
  };

  updateSubscription = (subscription, changedFields) => {
    if (changedFields.add_ons) {
      changedFields.add_ons = sanitizeAddOns(changedFields.add_ons);
    }

    return this.patch('/api/v1/subscription', {
      uuid: subscription.uuid,
      ...changedFields,
    });
  };

  updateSubscriptionPods = subscription => {
    return this.put('/api/v1/subscription_pod_swap', {
      uuid: subscription.uuid,
    });
  };

  convertSubscriptionToCartOrder = uuid => {
    return this.put('/api/v1/subscription_payment_source_update', { uuid });
  };

  cancelSubscription = (subscription, cancelReasons, cancelDetails) => {
    return this.post('/api/v1/subscription/cancel', {
      cancel_details: cancelDetails,
      cancel_reasons: cancelReasons,
    });
  };

  createMembership = () => {
    return this.post(MEMBERSHIPS_API, null);
  };

  updateMembership = changedFields => {
    return this.patch(MEMBERSHIPS_API, { ...changedFields });
  };

  fetchMembership = () => {
    return this.get(MEMBERSHIPS_API);
  };

  fetchRegisteredProducts = () => {
    return this.get('/api/v1/product_registrations');
  };

  fetchRegisteredProductsWarranty = productType => {
    return this.get(
      productType
        ? `/api/v1/product_registrations/warranty_eligible_by_product/${productType}`
        : '/api/v1/product_registrations/warranty_eligible'
    );
  };

  fetchWarrantySkus = productType => {
    return this.get(`/api/warranty/skus/${productType}`);
  };

  fetchWarrantyIssues = claimWarrantySkuId => {
    return this.get(`/api/warranty/issues/${claimWarrantySkuId}`);
  };

  createProductRegistration = product => {
    return this.post('/api/v1/product_registrations', {
      product: removeWhitespaceFromValues(product),
    });
  };

  createSmsSubscription = () => {
    return this.post('/api/v1/sms_subscription', null);
  };

  createSubscriptionQuote = addOns => {
    addOns = sanitizeAddOns(addOns);
    return this.post('/api/v1/subscription_quotes', { add_ons: addOns });
  };

  reportDevice = params => {
    return this.post(`/api/traceability/report_device`, params);
  };

  verifyRecaptchaToken = params => {
    return this.post(`/api/traceability/verify_recaptcha`, params);
  };

  fetchProfile = () => {
    return this.get('/api/v1/profile');
  };

  updateProfile = user => {
    return this.put('/api/v1/profile', {
      user: removeWhitespaceFromValues(user),
    });
  };

  fetchOrder = orderNumber => {
    return this.get(`/api/v1/orders/${orderNumber}`);
  };

  fetchCurrentOrder = () => {
    return this.get(`/api/v1/orders/current`);
  };

  fetchOrders = (params = {}) => {
    return this.get('/api/v1/orders', {}, params);
  };

  fetchOrderSummary = () => {
    return this.get('/api/v1/orders/summary');
  };

  fetchAvailablePaymentMethods = () => {
    return this.get('/api/v1/payment_methods');
  };

  fetchVariants = () => {
    return this.get(`/api/v1/variants`, {}, this.localeParams());
  };

  cancelOrder = orderNumber => {
    return this.post(`/api/v1/orders/${orderNumber}/cancel`);
  };

  changeOrderMembership = value => {
    return this.put('/api/v1/cart/membership', { membership_order: value });
  };

  updateCartLineItems = (lineItems, isCommitting) => {
    const data = { line_items: lineItems, committing: isCommitting };
    return this.put(`/api/v1/cart`, data, this.localeParams());
  };

  fetchCartLineItemsCount = () => {
    return this.get('/api/v1/cart/items_count');
  };

  fetchAgeVerification = () => {
    return this.get(`/api/v1/age_verification`);
  };

  updateAgeVerification = data => {
    const coercedAVData = new AgeVerificationUtility(data)
      .createDateOfBirth()
      .coerceAddressState()
      .value();

    return this.put(`/api/v1/age_verification`, coercedAVData);
  };

  ageVerificationReview = () => {
    return this.post(`/api/v1/age_verification/review`, '');
  };

  fetchClaimRequests = () => {
    return this.get('/api/v1/claim_requests');
  };

  fetchCurrentClaimRequest = () => {
    return this.get('/api/v1/claim_requests/current');
  };

  fetchClaimRequest = id => {
    return this.get(`/api/v1/claim_requests/${id}`);
  };

  fetchProcessingClaimItems = () => {
    return this.get('/api/v1/claim_items/processing');
  };

  createClaimRequest = createRequest => {
    return this.post('/api/v1/claim_requests', {
      claim_request: {
        claim_item: createRequest,
      },
    });
  };

  completeClaimRequest = (claimRequest, address) => {
    return this.post(`/api/v1/claim_requests/${claimRequest.id}/complete`, {
      claim_request: {
        ship_address_id: address.id,
      },
    });
  };

  cancelClaimRequest = claimRequest => {
    return this.del(`/api/v1/claim_requests/${claimRequest.id}`);
  };

  fetchBraintreeClientToken = () => {
    return this.post('/solidus_paypal_braintree/client_token.json', '');
  };

  fetchBraintreeThreeDSecureDetails = sourceId => {
    return this.get(`/braintree/payment_sources/threeds_details/${sourceId}`);
  };

  fetchPaymentSources = () => {
    return this.get('/api/v1/payment_sources');
  };

  fetchThreeDSecureDetails = sourceId => {
    return this.get(`/api/v1/payment_sources/3ds/${sourceId}`);
  };

  addPaymentSource = params => {
    return this.post(`/api/v1/payment_sources.json`, params);
  };

  updateCartAndClear = lineItems => {
    const data = { line_items: lineItems };
    return this.post('/api/v1/cart/clear_and_update', data);
  };

  startCheckout = location => {
    return this.post('/api/v1/checkout/start', {
      location,
      ...this.localeParams(),
    });
  };

  completeCheckout = params => {
    return this.post('/api/v1/checkout/complete', params);
  };

  offsiteCheckout = params => {
    return this.post('/api/v1/checkout/offsite', params);
  };

  recalculateOrder = params => {
    return this.post('/api/v1/checkout/recalculate_order', params);
  };

  fetchLegalPageContent = () => {
    return this.get(
      `/api/v1/contentful_entries/legal`,
      {},
      this.localeParams()
    );
  };

  fetchSupportFaqContent = () => {
    return this.get(
      `/api/v1/contentful_entries/support_questions`,
      {},
      this.localeParams()
    );
  };

  fetchBannerContent = () => {
    return this.get('/api/v1/contentful_entries/banner');
  };

  phoneVerificationSendCode = (phone_number, country_iso_code, via) => {
    return this.post('/api/v1/phone_verification/send_code', {
      phone_verification: {
        phone_number,
        country_iso_code,
        via,
      },
    });
  };

  phoneVerificationVerifyCode = (phone_number, country_iso_code, code) => {
    return this.post('/api/v1/phone_verification/verify_code', {
      phone_verification: {
        phone_number,
        country_iso_code,
        code,
      },
    });
  };

  phoneVerificationCheckVerified = () => {
    return this.get('/api/v1/phone_verification/check_verified');
  };

  sessionStateAbbr = value => {
    return this.put('/api/v1/session_state_abbr', { state_abbr: value });
  };

  fetchImpersonationStatus = () => {
    return this.get('/api/v1/impersonation');
  };

  sendRetailersSupportRequest = params => {
    return this.post('/api/v1/retailers_support', params);
  };

  validateExchangedDevice = params => {
    return this.post(
      '/api/v1/warranty_exchanges/validate_exchanged_device',
      params
    );
  };

  createDeviceWarrantyExchange = params => {
    return this.post(
      '/api/v1/warranty_exchanges/create_device_exchange',
      params
    );
  };

  createDeviceWarrantyExchangeWithout2FA = params => {
    const request = this.makeRequest(
      '/api/v1/warranty_exchanges/create_device_exchange_no_2fa',
      {
        method: 'POST',
        body: JSON.stringify(params),
      }
    );
    return this.handleResponse(request);
  };

  validateExchangedPod = params => {
    return this.post(
      '/api/v1/warranty_exchanges/validate_exchanged_pod',
      params
    );
  };

  createPodWarrantyExchange = params => {
    return this.post('/api/v1/warranty_exchanges/create_pod_exchange', params);
  };

  createPodWarrantyExchangeWithout2FA = params => {
    const request = this.makeRequest(
      '/api/v1/warranty_exchanges/create_pod_exchange_no_2fa',
      {
        method: 'POST',
        body: JSON.stringify(params),
      }
    );
    return this.handleResponse(request);
  };

  fetchFDKPaymentOptions = () => {
    return this.get('/api/v1/fdk/payment_options');
  };

  applyBusinessPromotion = params => {
    return this.post('/api/v1/checkout/apply_promotion', params);
  };

  convertOrderToSubscription = params => {
    return this.put('/api/v1/one_time_order_conversion', params);
  };

  fetchLossProtectionEligibleRegistrations = () => {
    return this.get('/api/v1/product_registrations/loss_protection_eligible');
  };

  createLossProtectionClaim = params => {
    return this.post('/api/v1/loss_protection/complete', params);
  };

  fetchAddressAutocompleteSuggestions = (address_query, container_id) => {
    return this.post('/api/v1/address_typeahead/find', {
      address_query,
      container_id,
    });
  };

  fetchAddressAutocompleteResult = address_id => {
    return this.post('/api/v1/address_typeahead/retrieve', { address_id });
  };

  fetchWarrantyExchangeSummary = params => {
    const queryParams = {
      end_date: params.endDate,
      start_date: params.startDate,
    };
    return this.get(`/api/v1/warranty_exchanges/summary`, {}, queryParams);
  };

  fetchWarrantyClaimsList = params => {
    const queryParams = {
      end_date: params.endDate,
      start_date: params.startDate,
    };
    return this.get(`/api/v1/warranty_exchanges/claims_list`, {}, queryParams);
  };

  fetchStorePurchaseLocations = () => {
    return this.get(`/api/v1/purchase_locations`);
  };

  createAdyenOriginKey = domain => {
    return this.post(`/api/juul_adyen/origin_key`, { domain });
  };

  fetchOrderSummaryByLocation = params => {
    const queryParams = { location_id: params.locationId };
    return this.get(
      `/api/v1/business_orders/summary_by_location`,
      {},
      queryParams
    );
  };

  createDeviceHealthCaseWarrantyExchange = params => {
    return this.post(
      '/api/v1/warranty_exchanges/create_device_health_case',
      params
    );
  };

  createPodHealthCaseWarrantyExchange = params => {
    return this.post(
      '/api/v1/warranty_exchanges/create_pod_health_case',
      params
    );
  };

  validateNameRequest = params => {
    return this.post('/age_verification/name_validation', params);
  };
}

const api = new ApiClient();

export default api;
